import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { submitPartnerLeadCall } from "../../../api/apiOperations";

import NavbarContainer from "../../../components/NavbarContainer";
import FooterSection from "../FooterSection";

import BunnyEars from "../../../assets/images/BunnyEars.png";

// Hero collage images
import heroImg1 from "../../../assets/images/business/images/OMEGA+Inside-22 (1).jpg";
import heroImg2 from "../../../assets/images/business/images/sqlpt brick 3.webp";
import heroImg3 from "../../../assets/images/business/images/rftlaudy.webp";
import heroImg4 from "../../../assets/images/business/images/3.jpg";

// Feature card images
import committedImg from "../../../assets/images/business/images/gotham2.jpg";
import setupImg from "../../../assets/images/business/images/pexels-olly-3757941 (1).jpg";
import revenueImg from "../../../assets/images/business/images/sqlptftl (1).jpg";

// Learn more collage images
import learnImg1 from "../../../assets/images/business/images/A7S04342 (1) (2).jpg";
import learnImg2 from "../../../assets/images/business/images/OMEGA+Inside-31 (1) (1).jpg";
import learnImg3 from "../../../assets/images/business/images/bwinenr.webp";
import learnImg4 from "../../../assets/images/business/images/pexels-krizalid99-14085139 (1).jpg";

// Process card images
import processImg1 from "../../../assets/images/business/images/sqlpt brick 2.webp";
import processImg2 from "../../../assets/images/business/images/IMG_1131 (1).jpg";
import processImg3 from "../../../assets/images/business/images/CKO Crew.jpg";

import {
  PageContainer,
  Input,
  Select,
  FieldRow,
  FieldFull,
  SubmitButton,
  ThankYouWrapper,
  ThankYouTitle,
  ThankYouSubtitle,
  BookingTitle,
  CalendlyBox,
  HeroSection,
  HeroInner,
  HeroLeftSide,
  HeroHeading,
  StatsGrid,
  StatBox,
  StatNumber,
  StatLabel,
  HeroRightSide,
  HeroCollage,
  CollageImg,
  FormCard,
  FormTitle,
  FormSubtitle,
  SuccessMessage,
  EngineSection,
  EngineInner,
  EngineSectionHeading,
  FeaturesRow,
  FeatureCard,
  FeatureImage,
  FeatureTitle,
  FeatureText,
  LearnMoreSection,
  LearnMoreInner,
  LearnMoreLeft,
  LearnMoreHeading,
  LearnMoreText,
  LearnMoreRightSide,
  ProcessSection,
  ProcessInner,
  ProcessTitle,
  ProcessGrid,
  ProcessCard,
  ProcessImage,
  ProcessCardTitle,
  ProcessCardText,
  FooterBox,
} from "./styles";

const LOCATION_OPTIONS = ["1", "2–5", "6–10", "10+"];

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const ListYourBusiness = () => {
  const [searchParams] = useSearchParams();
  // Main hero form state
  const heroFormRef = useRef<HTMLFormElement>(null);
  const [heroEmail, setHeroEmail] = useState("");
  const [heroPhone, setHeroPhone] = useState("");
  const [heroName, setHeroName] = useState("");
  const [heroBusiness, setHeroBusiness] = useState("");
  const [heroZip, setHeroZip] = useState("");
  const [heroLocations, setHeroLocations] = useState("");
  const [heroLoading, setHeroLoading] = useState(false);
  const [submitted, setSubmitted] = useState(searchParams.get("submitted") === "true");

  useEffect(() => {
    if (submitted) window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [submitted]);

  const [heroWebsite, setHeroWebsite] = useState("");

  // Learn more form state
  const learnFormRef = useRef<HTMLFormElement>(null);
  const [learnEmail, setLearnEmail] = useState("");
  const [learnPhone, setLearnPhone] = useState("");
  const [learnName, setLearnName] = useState("");
  const [learnBusiness, setLearnBusiness] = useState("");
  const [learnZip, setLearnZip] = useState("");
  const [learnLocations, setLearnLocations] = useState("");
  const [learnWebsite, setLearnWebsite] = useState("");
  const [learnLoading, setLearnLoading] = useState(false);
  const [learnSent, setLearnSent] = useState(false);

  const heroValid =
    !!heroEmail && heroPhone.replace(/\D/g, "").length >= 10 && !!heroName && !!heroBusiness && !!heroZip && !!heroLocations;

  const learnValid =
    !!learnEmail && learnPhone.replace(/\D/g, "").length >= 10 && !!learnName && !!learnBusiness && !!learnZip && !!learnLocations;

  const handleHeroSubmit = async () => {
    if (!heroValid) return;
    setHeroLoading(true);
    try {
      await submitPartnerLeadCall({
        name: heroName,
        businessName: heroBusiness,
        email: heroEmail,
        phone: heroPhone.replace(/\D/g, ""),
        zipCode: heroZip,
        locationCount: heroLocations,
        website: heroWebsite || undefined,
        source: "hero",
      });
      setSubmitted(true);
    } catch (_) {
    } finally {
      setHeroLoading(false);
    }
  };

  const handleLearnSubmit = async () => {
    if (!learnValid) return;
    setLearnLoading(true);
    try {
      await submitPartnerLeadCall({
        name: learnName,
        businessName: learnBusiness,
        email: learnEmail,
        phone: learnPhone.replace(/\D/g, ""),
        zipCode: learnZip,
        locationCount: learnLocations,
        website: learnWebsite || undefined,
        source: "learn_more",
      });
      setSubmitted(true);
    } catch (_) {
    } finally {
      setLearnLoading(false);
    }
  };

  if (submitted) {
    return (
      <PageContainer>
        <NavbarContainer dashboardScreen={false} checkoutScreen={false} darkText={true} />
        <ThankYouWrapper>
          <ThankYouTitle>Thank you for your submission!</ThankYouTitle>
          <ThankYouSubtitle>One of our team members will contact you shortly.</ThankYouSubtitle>
          <BookingTitle>Book a meeting to save time</BookingTitle>
          <CalendlyBox>
            <iframe
              src="https://calendly.com/matt-boise/jackedrabbit-nyc-partner-onboarding"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Book a meeting"
            />
          </CalendlyBox>
        </ThankYouWrapper>
        <FooterBox>
          <FooterSection />
        </FooterBox>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <NavbarContainer dashboardScreen={false} checkoutScreen={false} />

      {/* ── Hero ── */}
      <HeroSection>
        <HeroInner>
          <HeroLeftSide>
            <HeroHeading>Attract regulars,<br />not drop-ins.</HeroHeading>
            <StatsGrid>
              <StatBox>
                <StatNumber>30%+</StatNumber>
                <StatLabel>Trial to membership conversion potential</StatLabel>
              </StatBox>
              <StatBox>
                <StatNumber>30 days</StatNumber>
                <StatLabel>To build a relationship with new trials</StatLabel>
              </StatBox>
              <StatBox>
                <StatNumber>50+</StatNumber>
                <StatLabel>Brands trust and use JackedRabbit</StatLabel>
              </StatBox>
              <StatBox>
                <StatNumber>90%+</StatNumber>
                <StatLabel>Retention potential on in-app conversions</StatLabel>
              </StatBox>
            </StatsGrid>
          </HeroLeftSide>

          <HeroRightSide>
            <HeroCollage>
              <CollageImg pos="tl" src={heroImg1} alt="Gym interior" />
              <CollageImg pos="tr" src={heroImg2} alt="Fitness studio" />
              <CollageImg pos="bl" src={heroImg3} alt="Training" />
              <CollageImg pos="br" src={heroImg4} alt="Gym" />
            </HeroCollage>

            <FormCard>
              <FormTitle>List your business</FormTitle>
              <FormSubtitle>Fill out the form and we'll reach out to finalize your listing!</FormSubtitle>
              <form ref={heroFormRef}>
                <FieldFull>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={heroEmail}
                    onChange={(e) => setHeroEmail(e.target.value)}
                  />
                </FieldFull>
                <FieldFull>
                  <Input
                    type="tel"
                    placeholder="Phone"
                    value={heroPhone}
                    onChange={(e) => setHeroPhone(formatPhone(e.target.value))}
                  />
                </FieldFull>
                <FieldFull>
                  <Input
                    placeholder="Name"
                    value={heroName}
                    onChange={(e) => setHeroName(e.target.value)}
                  />
                </FieldFull>
                <FieldFull>
                  <Input
                    placeholder="Business name"
                    value={heroBusiness}
                    onChange={(e) => setHeroBusiness(e.target.value)}
                  />
                </FieldFull>
                <FieldRow>
                  <Input
                    placeholder="Zip code"
                    value={heroZip}
                    onChange={(e) => setHeroZip(e.target.value)}
                  />
                  <Select
                    value={heroLocations}
                    onChange={(e) => setHeroLocations(e.target.value)}
                  >
                    <option value="" disabled># of locations</option>
                    {LOCATION_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </Select>
                </FieldRow>
                <FieldFull>
                  <Input
                    placeholder="Website (optional)"
                    value={heroWebsite}
                    onChange={(e) => setHeroWebsite(e.target.value)}
                  />
                </FieldFull>
              </form>
              <SubmitButton onClick={handleHeroSubmit} disabled={!heroValid || heroLoading}>
                <img src={BunnyEars} alt="" style={{ width: 18, height: 18, borderRadius: "50%" }} />
                {heroLoading ? "Submitting..." : "Create your listing →"}
              </SubmitButton>
            </FormCard>
          </HeroRightSide>
        </HeroInner>
      </HeroSection>

      {/* ── Engine section ── */}
      <EngineSection>
        <EngineInner>
          <EngineSectionHeading>
            The engine that builds your community.<br />One month at a time.
          </EngineSectionHeading>
          <FeaturesRow>
            <FeatureCard>
              <FeatureImage src={committedImg} alt="Committed Members" />
              <FeatureTitle>Committed Members</FeatureTitle>
              <FeatureText>
                Every new member pays upfront and is committed to your location for a full month.
              </FeatureText>
            </FeatureCard>
            <FeatureCard>
              <FeatureImage src={setupImg} alt="Easy Set-up" />
              <FeatureTitle>Easy Set-up</FeatureTitle>
              <FeatureText>
                JackedRabbit syncs with your booking system so you can watch your community grow without lifting a finger.
              </FeatureText>
            </FeatureCard>
            <FeatureCard>
              <FeatureImage src={revenueImg} alt="Build Recurring Revenue" />
              <FeatureTitle>Build Recurring Revenue</FeatureTitle>
              <FeatureText>
                We turn month-long trials into long-term members, whether they upgrade in-app or directly with you.
              </FeatureText>
            </FeatureCard>
          </FeaturesRow>
        </EngineInner>
      </EngineSection>

      {/* ── Learn more section ── */}
      <LearnMoreSection>
        <LearnMoreInner>
          <LearnMoreLeft>
            <LearnMoreHeading>
              You focus on the experience.<br />We bring you committed leads.
            </LearnMoreHeading>
            <LearnMoreText>
              Our trials are designed to convert. By limiting users to a single location for the month,
              we give you real time to build relationships and turn trials into long-term members.
            </LearnMoreText>
          </LearnMoreLeft>

          <LearnMoreRightSide>
            <HeroCollage>
              <CollageImg pos="tl" src={learnImg1} alt="Training" />
              <CollageImg pos="tr" src={learnImg2} alt="Fitness" />
              <CollageImg pos="bl" src={learnImg3} alt="Workout" />
              <CollageImg pos="br" src={learnImg4} alt="Gym" />
            </HeroCollage>

            <FormCard>
              <FormTitle>Learn more</FormTitle>
              <FormSubtitle>See what JackedRabbit can do for your business</FormSubtitle>
              <form ref={learnFormRef}>
                <FieldFull>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={learnEmail}
                    onChange={(e) => setLearnEmail(e.target.value)}
                  />
                </FieldFull>
                <FieldFull>
                  <Input
                    type="tel"
                    placeholder="Phone"
                    value={learnPhone}
                    onChange={(e) => setLearnPhone(formatPhone(e.target.value))}
                  />
                </FieldFull>
                <FieldFull>
                  <Input
                    placeholder="Name"
                    value={learnName}
                    onChange={(e) => setLearnName(e.target.value)}
                  />
                </FieldFull>
                <FieldFull>
                  <Input
                    placeholder="Business name"
                    value={learnBusiness}
                    onChange={(e) => setLearnBusiness(e.target.value)}
                  />
                </FieldFull>
                <FieldRow>
                  <Input
                    placeholder="Zip code"
                    value={learnZip}
                    onChange={(e) => setLearnZip(e.target.value)}
                  />
                  <Select
                    value={learnLocations}
                    onChange={(e) => setLearnLocations(e.target.value)}
                  >
                    <option value="" disabled># of locations</option>
                    {LOCATION_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </Select>
                </FieldRow>
                <FieldFull>
                  <Input
                    placeholder="Website (optional)"
                    value={learnWebsite}
                    onChange={(e) => setLearnWebsite(e.target.value)}
                  />
                </FieldFull>
              </form>
              {learnSent ? (
                <SuccessMessage>Message sent! We'll be in touch soon.</SuccessMessage>
              ) : (
                <SubmitButton onClick={handleLearnSubmit} disabled={!learnValid || learnLoading}>
                  <img src={BunnyEars} alt="" style={{ width: 18, height: 18, borderRadius: "50%" }} />
                  {learnLoading ? "Sending..." : "Learn more →"}
                </SubmitButton>
              )}
            </FormCard>
          </LearnMoreRightSide>
        </LearnMoreInner>
      </LearnMoreSection>

      {/* ── Your process ── */}
      <ProcessSection>
        <ProcessInner>
          <ProcessTitle>Working with us</ProcessTitle>
          <ProcessGrid>
            <ProcessCard>
              <ProcessImage src={processImg1} alt="Tell us about your business" />
              <ProcessCardTitle>Tell us about your business</ProcessCardTitle>
              <ProcessCardText>
                Fill out the form above and our team will handle the rest.
              </ProcessCardText>
            </ProcessCard>
            <ProcessCard>
              <ProcessImage src={processImg2} alt="Go live" />
              <ProcessCardTitle>Go live</ProcessCardTitle>
              <ProcessCardText>
                Post your listing, connect your systems, and start bringing in new faces.
              </ProcessCardText>
            </ProcessCard>
            <ProcessCard>
              <ProcessImage src={processImg3} alt="Ongoing support" />
              <ProcessCardTitle>Ongoing support</ProcessCardTitle>
              <ProcessCardText>
                Through our systems and marketing campaigns, we work alongside your team to help convert trials into members.
              </ProcessCardText>
            </ProcessCard>
          </ProcessGrid>
        </ProcessInner>
      </ProcessSection>

      <FooterBox>
        <FooterSection />
      </FooterBox>
    </PageContainer>
  );
};

export default ListYourBusiness;
